/**
 * Send Reminder Dialog Component
 * Dialog for sending reminder emails to customers for overdue invoices
 * US-INV-004: AC-5 - Send reminder email
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Mail, AlertCircle } from 'lucide-react';
import { invoiceService } from '@/services/commerce/invoiceService';
import type { OverdueInvoiceSummary } from '@/types/invoice';
import { toast } from 'sonner';

interface SendReminderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: OverdueInvoiceSummary;
  onSuccess?: () => void;
}

export const SendReminderDialog: React.FC<SendReminderDialogProps> = ({
  open,
  onOpenChange,
  invoice,
  onSuccess,
}) => {
  const [sending, setSending] = useState(false);

  const handleSendReminder = async () => {
    try {
      setSending(true);
      await invoiceService.sendReminderEmail(invoice.invoiceId);
      toast.success(`Reminder sent to ${invoice.customerName}`);
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      console.error('Error sending reminder:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to send reminder');
    } finally {
      setSending(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Send Payment Reminder
          </DialogTitle>
          <DialogDescription>
            Send an email reminder to the customer about their overdue invoice.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Invoice Details */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Invoice Number:</span>
              <span className="text-sm font-semibold">{invoice.invoiceNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Customer:</span>
              <span className="text-sm font-semibold">{invoice.customerName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Email:</span>
              <span className="text-sm font-medium text-blue-600">
                {invoice.customerEmail || 'No email on file'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Amount Due:</span>
              <span className="text-sm font-bold text-red-600">
                {formatCurrency(invoice.balanceDue)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Due Date:</span>
              <span className="text-sm font-semibold">{formatDate(invoice.dueDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Days Overdue:</span>
              <span className="text-sm font-bold text-orange-600">{invoice.daysOverdue} days</span>
            </div>
          </div>

          {/* Warning if no email */}
          {!invoice.customerEmail && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">No email address on file</p>
                <p className="text-xs mt-1">
                  Please update the customer's email address before sending a reminder.
                </p>
              </div>
            </div>
          )}

          {/* Email Preview */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Email Preview:</p>
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
              <p className="font-medium mb-2">Subject: Payment Reminder - Invoice {invoice.invoiceNumber}</p>
              <p className="mb-2">Dear {invoice.customerName},</p>
              <p className="mb-2">
                This is a friendly reminder that your payment for invoice {invoice.invoiceNumber} is
                overdue by {invoice.daysOverdue} days.
              </p>
              <p className="mb-2">
                <strong>Amount Due:</strong> {formatCurrency(invoice.balanceDue)}<br />
                <strong>Due Date:</strong> {formatDate(invoice.dueDate)}
              </p>
              <p className="mb-2">
                Please process your payment at your earliest convenience to avoid any late fees.
              </p>
              <p>Best regards,<br />SAUBHAGYA Finance Team</p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendReminder}
            disabled={sending || !invoice.customerEmail}
            className="gap-2"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Sending...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4" />
                Send Reminder
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
